import React from 'react';
import { Link, NavLink } from 'react-router-dom';
import styled from 'styled-components';
import { colors, fonts, focusRing, radius } from '../theme';

const HeaderContainer = styled.header`
  background: ${colors.paper};
  border-bottom: 1px solid ${colors.ruleStrong};
`;

const HeaderContent = styled.div`
  max-width: 1180px;
  margin: 0 auto;
  padding: 0 20px;
  height: 58px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
`;

const Logo = styled(Link)`
  font-family: ${fonts.display};
  font-size: 21px;
  font-weight: 700;
  letter-spacing: -0.01em;
  color: ${colors.ink};
  text-decoration: none;
  white-space: nowrap;
  ${focusRing}

  /* The square stands in for a crossword cell. */
  &::before {
    content: '';
    display: inline-block;
    width: 11px;
    height: 11px;
    margin-right: 9px;
    background: ${colors.accent};
    vertical-align: baseline;
  }
`;

const Nav = styled.nav`
  display: flex;
  gap: 4px;
  align-items: center;
`;

const NavItem = styled(NavLink)`
  position: relative;
  color: ${colors.inkSoft};
  text-decoration: none;
  font-size: 14px;
  font-weight: 500;
  padding: 6px 10px;
  border-radius: ${radius.sm};
  transition: color 0.12s ease;
  ${focusRing}

  &:hover {
    color: ${colors.ink};
  }

  &.active {
    color: ${colors.ink};
    font-weight: 600;
  }

  /* Active route gets a short rule underneath rather than a filled pill. */
  &.active::after {
    content: '';
    position: absolute;
    left: 10px;
    right: 10px;
    bottom: -1px;
    height: 2px;
    background: ${colors.accent};
  }

  @media (max-width: 560px) {
    padding: 6px 7px;
    font-size: 13px;
  }
`;

const AuthButton = styled.button`
  background: none;
  border: none;
  padding: 6px 10px;
  margin-left: 4px;
  font-size: 14px;
  font-weight: 500;
  color: ${colors.inkSoft};
  cursor: pointer;
  border-radius: ${radius.sm};
  ${focusRing}

  &:hover {
    color: ${colors.accent};
  }

  @media (max-width: 560px) {
    padding: 6px 7px;
    font-size: 13px;
  }
`;

interface HeaderProps {
  currentUser?: { email: string; display_name?: string | null } | null;
  onSignIn?: () => void;
  onSignOut?: () => void;
}

export const Header: React.FC<HeaderProps> = ({ currentUser, onSignIn, onSignOut }) => {
  return (
    <HeaderContainer>
      <HeaderContent>
        <Logo to="/">Mini Crossword</Logo>

        <Nav>
          <NavItem to="/" end>Today</NavItem>
          <NavItem to="/leaderboard">Leaderboard</NavItem>
          <NavItem to="/archive">Archive</NavItem>
          {currentUser && <NavItem to="/stats">Stats</NavItem>}
          {currentUser ? (
            <AuthButton onClick={onSignOut} title={currentUser.email}>
              Sign out
            </AuthButton>
          ) : (
            <AuthButton onClick={onSignIn}>Sign in</AuthButton>
          )}
        </Nav>
      </HeaderContent>
    </HeaderContainer>
  );
};

export default Header;
